import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Star, Mail, Trophy, X, CheckCircle2, AlertCircle, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { EXPERIENCE_DATA } from '@/components/booking/bookingDefaults';
import { fmtDate } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/lib/AuthContext';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// Reviews and customer feedback are one destination in the sidebar, joined by
// this strip. Each keeps its own route, data and actions.
const SECTION_TABS = [
  { to: '/admin/reviews', label: 'REVIEWS' },
  { to: '/admin/feedback', label: 'CUSTOMER FEEDBACK' },
];

const STATUS = {
  pending: { label: 'PENDING', cls: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
  reviewed: { label: 'REVIEWED', cls: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  draw_entered: { label: 'DRAW ENTERED', cls: 'text-primary bg-primary/10 border-primary/20' },
  winner: { label: 'WINNER', cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  archived: { label: 'ARCHIVED', cls: 'text-white/30 bg-white/5 border-white/10' },
};

const RATING_ROWS = [
  { key: 'q_overall', label: 'Overall Experience' },
  { key: 'q_comfort', label: 'Comfort & rig setup' },
  { key: 'q_controls', label: 'Wheel, pedals & controls' },
  { key: 'q_explanation', label: 'Equipment explanation' },
  { key: 'q_staff', label: 'Staff friendliness' },
  { key: 'q_guidance', label: 'Guidance throughout' },
  { key: 'q_value', label: 'Value for money' },
  { key: 'q_recommend', label: 'Likelihood to recommend' },
  { key: 'q_rebook', label: 'Likelihood to rebook' },
];

function Stat({ label, value, icon }) {
  return (
    <div className="bg-[#111115] border border-white/5 p-5">
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2 flex items-center gap-1.5">
        {icon}
        {label}
      </p>
      <p className="text-white font-black text-2xl">{value}</p>
    </div>
  );
}

function Detail({ label, value, block }) {
  return (
    <div className={block ? '' : 'flex flex-col'}>
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">{label}</p>
      <p className="text-white/80 text-sm whitespace-pre-wrap">{value || '—'}</p>
    </div>
  );
}

function ScoreRow({ label, n }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <span className="text-white/60 text-xs">{label}</span>
      <span className={`font-mono font-bold text-sm ${n >= 8 ? 'text-emerald-400' : n >= 5 ? 'text-white' : 'text-amber-400'}`}>
        {n || 0}<span className="text-white/30 text-xs"> / 10</span>
      </span>
    </div>
  );
}

function FeedbackModal({ feedback, onClose, onUpdated }) {
  const [status, setStatus] = useState(feedback.status || 'pending');
  const [notes, setNotes] = useState(feedback.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const save = async () => {
    setSaving(true);
    try {
      await base44.entities.ExperienceFeedback.update(feedback.id, { status, admin_notes: notes });
      onUpdated();
    } catch (e) {
      toast({ title: 'Could not save feedback', description: e.message || 'Please try again.', variant: 'destructive' });
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#111115] border border-white/10 max-w-2xl w-full p-6 md:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-white font-black text-xl uppercase">
              {feedback.first_name} {feedback.last_name}
            </h3>
            <a href={`mailto:${feedback.email}`} className="text-primary text-xs hover:underline">{feedback.email}</a>
            {feedback.phone && <p className="text-white/40 text-xs mt-0.5">{feedback.phone}</p>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          <Detail label="BOOKING REFERENCE" value={feedback.booking_reference} />
          <Detail label="EXPERIENCE" value={EXPERIENCE_DATA[feedback.experience_type]?.name || feedback.experience_type} />
          <Detail label="DATE OF EXPERIENCE" value={fmtDate(feedback.date_of_experience) || '—'} />
          <Detail label="SUBMITTED" value={fmtDate(feedback.created_date) || '—'} />
        </div>

        <div className="mb-6">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">RATINGS</p>
          <div className="bg-[#0d0d0f] border border-white/5 px-4">
            {RATING_ROWS.map((r) => (
              <ScoreRow key={r.key} label={r.label} n={feedback[r.key]} />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {feedback.improvements && <Detail label="WHAT COULD WE IMPROVE" value={feedback.improvements} block />}
          {feedback.further_comments && <Detail label="FURTHER COMMENTS" value={feedback.further_comments} block />}
        </div>

        <div className="mt-6 pt-4 border-t border-white/5">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">MAILCHIMP SYNC</p>
          {feedback.mailchimp_synced ? (
            <p className="text-emerald-400 text-xs flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Synced to audience {feedback.mailchimp_list_id}</p>
          ) : feedback.mailchimp_error ? (
            <p className="text-red-400 text-xs flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5" /> {feedback.mailchimp_error}</p>
          ) : (
            <p className="text-white/30 text-xs">Not synced (no newsletter consent)</p>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-white/5 space-y-4">
          <div>
            <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-2">STATUS</label>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS).map((k) => (
                <button
                  key={k}
                  onClick={() => setStatus(k)}
                  className={`px-3 py-1.5 text-[10px] font-bold tracking-widest border transition-colors ${
                    status === k ? 'bg-primary border-primary text-white' : 'border-white/10 text-white/50 hover:border-primary/40'
                  }`}
                >
                  {STATUS[k].label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-2">ADMIN NOTES</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-4 py-3 outline-none resize-none"
            />
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm tracking-widest px-6 py-3 transition-colors disabled:opacity-50"
          >
            {saving ? 'SAVING…' : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const qc = useQueryClient();

  const { data: feedback = [], isLoading } = useQuery({
    queryKey: ['experience-feedback'],
    queryFn: () => base44.entities.ExperienceFeedback.list('-created_date', 200),
    enabled: currentUser?.role === 'admin',
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
        </div>
      </div>
    );
  }

  const avg = (key) => {
    const vals = feedback.filter((f) => f[key] > 0).map((f) => f[key]);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : '–';
  };
  const applyDate = (f) => {
    if (dateFilter === 'all') return true;
    const iso = (f.created_date || '').slice(0, 10);
    if (!iso) return false;
    const d = new Date(iso + 'T00:00:00');
    const now = new Date(); now.setHours(0, 0, 0, 0);
    if (dateFilter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    const days = dateFilter === '30' ? 30 : 90;
    const cutoff = new Date(now); cutoff.setDate(now.getDate() - days);
    return d >= cutoff;
  };
  const q = search.toLowerCase();
  const filtered = feedback.filter((f) => {
    if (filter !== 'all' && f.status !== filter) return false;
    if (!applyDate(f)) return false;
    if (!q) return true;
    return (f.first_name || '').toLowerCase().includes(q)
      || (f.last_name || '').toLowerCase().includes(q)
      || (f.email || '').toLowerCase().includes(q)
      || (f.booking_reference || '').toLowerCase().includes(q);
  });
  const marketingCount = feedback.filter((f) => f.consent_marketing).length;
  const drawCount = feedback.filter((f) => f.consent_draw).length;
  const syncedCount = feedback.filter((f) => f.mailchimp_synced).length;

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">FEEDBACK &amp; SURVEY RESULTS</h1>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
        <AdminSectionTabs tabs={SECTION_TABS} className="mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Stat label="TOTAL RESPONSES" value={feedback.length} />
          <Stat label="AVG OVERALL" value={avg('q_overall')} />
          <Stat label="AVG RECOMMEND" value={avg('q_recommend')} />
          <Stat label="NEWSLETTER OPT-INS" value={marketingCount} icon={<Mail className="w-3.5 h-3.5" />} />
          <Stat label="DRAW ENTRIES" value={drawCount} icon={<Trophy className="w-3.5 h-3.5" />} />
          <Stat label="SYNCED TO MAILCHIMP" value={syncedCount} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or booking ref..." className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60" />
          </div>
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="appearance-none bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none">
            <option value="all">All time</option>
            <option value="month">This month</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { k: 'all', l: 'All' },
            { k: 'pending', l: 'Pending' },
            { k: 'reviewed', l: 'Reviewed' },
            { k: 'draw_entered', l: 'Draw Entered' },
            { k: 'winner', l: 'Winner' },
            { k: 'archived', l: 'Archived' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`px-4 py-2 text-xs font-bold tracking-wider border transition-colors ${
                filter === t.k ? 'bg-primary border-primary text-white' : 'border-white/10 text-white/40 hover:border-primary/40'
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Star className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No feedback entries yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((f) => {
              const st = STATUS[f.status] || STATUS.pending;
              return (
                <button
                  key={f.id}
                  onClick={() => setSelected(f)}
                  className="w-full text-left bg-[#111115] border border-white/5 hover:border-primary/40 p-5 transition-colors flex flex-col md:flex-row md:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-white font-bold text-sm truncate">{f.first_name} {f.last_name}</p>
                      <span className="font-mono font-bold text-sm text-primary">{f.q_overall || 0}<span className="text-white/30 text-xs">/10</span></span>
                    </div>
                    <p className="text-white/30 text-xs truncate">{f.email} · Booking {f.booking_reference}</p>
                    {f.improvements && <p className="text-white/40 text-xs mt-1 line-clamp-1 italic">&ldquo;{f.improvements}&rdquo;</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {f.consent_draw && <span className="text-[10px] font-bold tracking-widest text-primary border border-primary/30 bg-primary/10 px-2 py-1">DRAW</span>}
                    {f.consent_marketing && <span className="text-[10px] font-bold tracking-widest text-blue-400 border border-blue-400/30 bg-blue-400/10 px-2 py-1">NEWSLETTER</span>}
                    <span className={`text-[10px] font-bold tracking-widest border px-2 py-1 ${st.cls}`}>{st.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {selected && (
        <FeedbackModal
          feedback={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            qc.invalidateQueries({ queryKey: ['experience-feedback'] });
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}